import { TDemoAIFillingGroupedStep } from '@commenda/types';
import { TaskStatus } from '@prisma/client';
import { useMemo } from 'react';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { ServiceRequestTaskLabel } from '../../labels';
import { ServiceRequestTaskIconConfig } from '../../taskIconConfig';
import {
  AnimatedGroupedTimeline,
  CollapsibleHistory,
  CompletedGroupedTimelineView,
} from './shared/TimelineComponents';

const LLM_GROUPED_STEPS: TDemoAIFillingGroupedStep[] = [
  {
    title: 'Analyzing product catalog',
    titleDelayMs: 0,
    substepDelayMs: 2500,
    substeps: [
      'Loaded product catalog from Commenda',
      'Identified 1 product requiring tax code assignment',
      'Categorized product by type and jurisdiction',
    ],
  },
  {
    title: 'Retrieving taxability rules',
    titleDelayMs: 3000,
    substepDelayMs: 3000,
    substeps: [
      'Accessed Commenda product taxability documentation',
      'Loaded state-specific tax exemption rules',
      'Identified applicable tax category for product',
    ],
  },
  {
    title: 'Mapping tax code to product',
    titleDelayMs: 3500,
    substepDelayMs: 2500,
    substeps: [
      'Matched product to tax category',
      'Assigned tax code based on product attributes',
      'Verified tax code assignment against jurisdiction rules',
    ],
  },
  {
    title: 'Applying tax code assignment',
    titleDelayMs: 3000,
    substepDelayMs: 2000,
    substeps: [
      'Saved tax code to product catalog',
      'Updated taxability status for product',
      'Product tax mapping completed successfully',
    ],
  },
];

export const WorkflowLLMTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const isCompleted = task.status === TaskStatus.COMPLETED;

  const statusLabel = useMemo(() => {
    if (isCompleted) return `${task.title} completed`;
    return task.title;
  }, [isCompleted, task.title]);

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: () => ({
      actions: null,
      label: (
        <ServiceRequestTaskLabel
          label={statusLabel}
          status={task.status}
          timestamp={isCompleted ? task.updatedAt : undefined}
        />
      ),
    }),
  };

  const isDemo =
    process.env.NEXT_PUBLIC_CLIENT_ENV === 'demo' ||
    process.env.NEXT_PUBLIC_CLIENT_ENV === 'development';
  const showTimeline = isDemo && !isCompleted;

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role: context.role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      {showTimeline && (
        <div className="border border-fg-brand rounded-md mt-4 p-4 bg-bg-primary shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)]">
          <AnimatedGroupedTimeline steps={LLM_GROUPED_STEPS} />
        </div>
      )}
      {isCompleted && (
        <div className="border border-border-secondary rounded-md mt-4 px-6 py-1 bg-bg-primary shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)]">
          <CollapsibleHistory>
            <CompletedGroupedTimelineView steps={LLM_GROUPED_STEPS} />
          </CollapsibleHistory>
        </div>
      )}
    </div>
  );
};
